import axios from "axios";
import { goToLogin, goTo403, goTo2faSetup } from "@/utils/Navigation";
import { toast } from "sonner";
import { transformUuidToId } from "@/utils/transformUuidToId";
import { getTenantBaseUrl } from "@/utils/tenant";

const SERVER_IP = getTenantBaseUrl();
const apiService = axios.create({
  baseURL: SERVER_IP,
  withXSRFToken: true,
  withCredentials: true,
  origin: true,
});

// One-shot 401 toast flag
let unauthorizedToastShown = false;

apiService.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    // don't attach bearer to login endpoint
    if (token && !(config.url || "").endsWith("/login")) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // content-type handling
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"]; // browser sets boundary
    } else if (config.data instanceof Blob) {
      // Raw bytes, e.g. one piece of a chunked upload.
      config.headers["Content-Type"] = "application/octet-stream";
    } else {
      config.headers["Content-Type"] = "application/json";
    }

    if ((config).isPdf) {
      config.headers["Accept"] = "application/pdf";
      config.headers["Content-Type"] = "application/pdf";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiService.interceptors.response.use(
  //  success path
  (response) => {
    // if a login just succeeded, allow future 401 toasts again
    const url = response.config?.url || "";
    if (/\/(login|auth|oauth|sanctum\/token)\b/.test(url)) {
      unauthorizedToastShown = false;
    }

    // Transform uuid > id
    console.log("Raw response data:", response);
    if (response.data && response.config.responseType !== 'blob') {
      response.data = transformUuidToId(response.data);
      console.log("Transformed response data:", response.data);
    }
    return response;
  },

  // error path
  (error) => {
    // Network error or no response at all
    if (!error.response) {
      return Promise.reject({
        customMessage:
          "Network error. Please check your connection and try again.",
        original: error,
      });
    }

    const { status, data } = error.response;
    const url = error.config?.url || "";
    const isAuthRoute = /^(?!\/auth\/logout)(\/(login|auth|oauth|sanctum\/token)\b)/.test(url);

    // 403 — access restriction
    if (status === 403) {
      // The firm requires 2FA and this user has not set it up (OL-157):
      // send them where they can, instead of to the access-denied page.
      if (data?.code === "two_factor_setup_required") {
        goTo2faSetup();
        return Promise.reject({ customMessage: data.message });
      }

      const message =
        data?.message ||
        data?.error ||
        "Access denied. You don't have permission to perform this action.";

      // Pass specific inactive cases to the caller to render inline
      if (
        message === "Your account is inactive. Please contact support." ||
        message === "Your role is inactive. Contact admin." ||
        message === "Access denied. Request rejected."
      ) {
        return Promise.reject({ customMessage: message });
      }

      // Otherwise route to 403 page
      goTo403();
      return Promise.reject({ customMessage: message });
    }

    // 401 / 419 — unauthorized / expired session
    if (status === 401 || status === 419) {
      console.log('status --->', status);

      // Special case: let auth endpoints (e.g., wrong creds on /login) bubble up
      if (isAuthRoute) {
        // IMPORTANT: do not swallow; let the page re-enable the button & show message
        const message =
          data?.message || "Invalid credentials. Please try again.";
        console.log('in if --->');

        return Promise.reject({ customMessage: message, response: error.response });
      }

      console.log('outside if --->');

      // Global handling for non-auth routes (expired token, csrf, etc.)
      localStorage.removeItem("access_token");
      sessionStorage.removeItem("access_token");
      localStorage.removeItem("user");
      goToLogin()
      // Avoid redirect loops if already at login
      // try {
      //   const onLoginPage = window?.location?.pathname?.includes("/login");
      //   if (!onLoginPage) ;
      // } catch {
      //   // no-op (SSR/unit tests)
      // }

      if (!unauthorizedToastShown) {
        unauthorizedToastShown = true;
        toast.error("Unauthorized Access", {
          description:
            "Your session has timed out for security reasons. Please sign in again to continue.",
        });
      }

      // SWALLOW only the global 401 to prevent duplicate toasts in components
      return new Promise(() => { });
    }

    // Other errors — just propagate
    return Promise.reject(error);
  }
);

export default apiService;
